import { useCallback, useMemo } from "react";
import { Box, Button, Group, Image, Stack, Text } from "@mantine/core";
import { IconFile } from "@tabler/icons-react";
import { REPORT_IMAGE_ROLE } from "../../utils/reportImageAssets";
import { PdfPagesRenderer } from "./PdfPagesRenderer";
import { isImageMimeType } from "../../utils/reportAttachments";
import {
  ReportAttachmentPage,
  type ReportFooterProps,
  type ReportHeaderSectionProps,
} from "./ReportAttachmentPage";

export interface ReportFileAttachment {
  id: string;
  name: string;
  mimeType: string;
  label?: string | null;
  finalizedInReport?: boolean;
}

export function isRenderableReportFile(file: ReportFileAttachment): boolean {
  return isImageMimeType(file.mimeType) || file.mimeType === "application/pdf";
}

interface ReportFilePageProps {
  file: ReportFileAttachment;
  url?: string;
  failed?: boolean;
  onLoadError?: (fileId: string) => void;
  onLoadComplete?: (fileId: string, pageCount?: number) => void;
  onRetry?: (fileId: string) => void;
  allowExternalLink?: boolean;
  headerSectionProps?: ReportHeaderSectionProps;
  footerProps?: Omit<ReportFooterProps, "pageNumber" | "totalPages">;
  firstPageNumber?: number;
  totalPages?: number;
}

export function ReportFilePage({
  file,
  url,
  failed,
  onLoadError,
  onLoadComplete,
  onRetry,
  allowExternalLink = true,
  headerSectionProps,
  footerProps,
  firstPageNumber,
  totalPages,
}: ReportFilePageProps) {
  const isImage = useMemo(
    () => isImageMimeType(file.mimeType),
    [file.mimeType]
  );
  const isPdf = useMemo(
    () => file.mimeType === "application/pdf",
    [file.mimeType]
  );
  const handleRetry = useCallback(() => onRetry?.(file.id), [file.id, onRetry]);
  const handleLoadError = useCallback(
    () => onLoadError?.(file.id),
    [file.id, onLoadError]
  );
  const handleImageLoadComplete = useCallback(
    () => onLoadComplete?.(file.id, 1),
    [file.id, onLoadComplete]
  );
  const handlePdfLoadComplete = useCallback(
    (pageCount: number) => onLoadComplete?.(file.id, pageCount),
    [file.id, onLoadComplete]
  );
  const numberedFooterProps = useMemo<ReportFooterProps | undefined>(
    () =>
      footerProps && firstPageNumber !== undefined && totalPages !== undefined
        ? { ...footerProps, pageNumber: firstPageNumber, totalPages }
        : undefined,
    [firstPageNumber, footerProps, totalPages]
  );

  if (isPdf && url && !failed) {
    return (
      <PdfPagesRenderer
        url={url}
        label={file.name}
        onError={handleLoadError}
        onReady={handlePdfLoadComplete}
        reportPageFrame={
          firstPageNumber !== undefined && totalPages !== undefined
            ? {
                headerSectionProps,
                footerProps,
                firstPageNumber,
                totalPages,
              }
            : undefined
        }
      />
    );
  }

  return (
    <ReportAttachmentPage
      headerSectionProps={headerSectionProps}
      footerProps={numberedFooterProps}
    >
      {failed ? (
        <Stack align="center" gap="xs" py="lg">
          <Text c="red" size="sm">
            This report attachment could not be loaded.
          </Text>
          {onRetry && (
            <Button variant="light" onClick={handleRetry} mih={44}>
              Retry attachment
            </Button>
          )}
        </Stack>
      ) : isImage && url ? (
        <Box style={{ textAlign: "center", width: "100%" }}>
          <Image
            src={url}
            alt={file.name}
            data-report-image-role={REPORT_IMAGE_ROLE.clinicalAttachment}
            fit="contain"
            mah={600}
            w="100%"
            onError={handleLoadError}
            onLoad={handleImageLoadComplete}
          />
          <Text size="xs" c="dimmed" ta="center" mt={4}>
            {file.name}
          </Text>
        </Box>
      ) : (
        <Group
          gap="xs"
          py="sm"
          px="md"
          style={{
            border: "1px solid var(--mantine-color-gray-3)",
            borderRadius: 4,
          }}
        >
          <IconFile size={18} color="var(--mantine-color-gray-5)" />
          <Text size="sm">
            {url && allowExternalLink ? (
              <a href={url} target="_blank" rel="noopener noreferrer">
                {file.name}
              </a>
            ) : (
              file.name
            )}
          </Text>
        </Group>
      )}
    </ReportAttachmentPage>
  );
}
